/**
 * Ensures MM-DD-YYYY style format (replaces slash with hyphen).
 */

import dayjs, { Dayjs } from "dayjs";
import { DateFormat } from "../constants/dateFormat";

//@ts-ignore
const dateFormatter = (dateStr: string): string => dateStr.replaceAll("/", "-");

// Parse date range value from the format YYYY-MM-DD,YYYY-MM-DD to [Dayjs, Dayjs]
const parseDateRangeValue = (value: string | null): [Dayjs, Dayjs] | null => {
    if (!value) return null;

    const [start, end] = value.split(',').map((date) => date.trim());
    
    if (!start || !end) return null;
    
    const startDate = dayjs(start, DateFormat.YYYY_MM_DD);
    const endDate = dayjs(end, DateFormat.YYYY_MM_DD);

    if (!startDate.isValid() || !endDate.isValid()) return null;
    
    return [startDate, endDate];
};

const formatUTCDate = ({
    dateString,
    addZ = true,
    dateMonthYear = false
}: {
    dateString: string,
    addZ?: boolean,
    dateMonthYear?: boolean
}): string => {
    if (!dateString) return "N/A"; // Handle null/undefined/empty cases

    const dateObj = new Date(dateString + (addZ ? "Z" : "")); // Ensure it's treated as UTC
    if (isNaN(dateObj.getTime())) return "Invalid Date"; // Handle invalid dates

    // Convert to local time zone and format
    return dateFormatter(
        dateObj.toLocaleDateString(undefined, {
            year: "numeric",
            month: dateMonthYear ? "short" : "2-digit",
            day: "2-digit",
        })
    );
};

const formatUTCDateTime = (dateString: string, includeSeconds: boolean = true, readable: boolean = false): string => {
    if (!dateString) return "N/A";

    // Only append "Z" when the string has no timezone. Regex matches trailing offset: [+-]HH:MM or [+-]HHMM (e.g. +00:00, -05:30).
    const hasTimezone =
        dateString.endsWith("Z") || /[+-]\d{2}:?\d{2}$/.test(dateString);
    const normalized = hasTimezone ? dateString : dateString + "Z";

    const dateObj = new Date(Date.parse(normalized));

    if (isNaN(dateObj.getTime())) return "Invalid Date";

    const options: Intl.DateTimeFormatOptions = {
        year: "numeric",
        month: readable ? "short" : "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        ...(readable && { hour12: true }),
        ...(includeSeconds && { second: "2-digit" }),
    };

    const formatted = readable
        ? dateObj.toLocaleDateString(undefined, options).replace(",", "")
        : dateFormatter(dateObj.toLocaleDateString(undefined, options));

    return formatted;
};

export { formatUTCDate, formatUTCDateTime, parseDateRangeValue };